import React, { useRef } from 'react';
import { useTooltipTriggerState } from '@react-stately/tooltip';
import { Overlay, useTooltip, useTooltipTrigger, useOverlay, useOverlayPosition, mergeProps } from 'react-aria';
import type { Placement } from '@react-types/overlays';
import classnames from 'classnames';

interface TooltipProps {
	children: React.ReactElement;
	label: string;
	placement?: Placement;
	offset?: number;
	wrap?: boolean;
}

export default function Tooltip(props: TooltipProps) {
	const { label, placement, offset, wrap = false } = props;

	const triggerRef = useRef<HTMLDivElement>(null);
	const state = useTooltipTriggerState({ delay: 0 });
	const { triggerProps, tooltipProps } = useTooltipTrigger({}, state, triggerRef);
	const { tooltipProps: tooltipElementProps } = useTooltip(tooltipProps, state);

	const overlayRef = useRef<HTMLDivElement>(null);

	const { overlayProps } = useOverlay(
		{
			isOpen: state.isOpen,
			onClose: state.close,
			shouldCloseOnBlur: true,
			isDismissable: true,
			isKeyboardDismissDisabled: false,
		},
		overlayRef,
	);

	const { overlayProps: positionProps } = useOverlayPosition({
		targetRef: triggerRef,
		overlayRef: overlayRef,
		isOpen: state.isOpen,
		onClose: state.close,
		placement: placement || 'right',
		offset: offset || 2,
	});

	return (
		<div style={{ position: 'relative', display: 'inline-block' }}>
			<div ref={triggerRef}>
				{React.cloneElement(props.children, { ...triggerProps, ...props.children.props })}
			</div>
			{state.isOpen && (
				<Overlay>
					<div ref={overlayRef} {...mergeProps(overlayProps, positionProps)}>
						<span
							className={classnames('roc-tooltip', {
								'roc-tooltip--wrap': wrap,
							})}
							{...tooltipElementProps}
						>
							{label}
						</span>
					</div>
				</Overlay>
			)}
		</div>
	);
}
